// src/vaultTransfer.js
//
// Export / import for the Password Manager, using Bitwarden's own JSON format
// ("Bitwarden (json)", unencrypted) so one file works in both directions:
//   - Export here  -> File > Import data > "Bitwarden (json)" in Bitwarden
//   - Export from Bitwarden (".json", NOT "encrypted json") -> Import here
//
// Everything runs in the browser, on entries that are already decrypted in
// memory. Nothing in this file talks to Firestore or the network.
//
// Mapping (CRM type -> Bitwarden item):
//   login                     -> Login   (username, password, one URL, notes)
//   github_pat / api_key /    -> Secure note. The secret is the note text, and
//   firebase_service_account     the rest is kept in custom fields:
//                                crm_type, username, url, crm_notes
// The custom field "crm_type" lets a file made by this screen come back with
// the same types. Files that come from elsewhere are guessed (see guessType).

export const DEFAULT_TYPE = "login";
export const KNOWN_TYPES = ["login", "github_pat", "api_key", "firebase_service_account"];

const BW_LOGIN = 1;
const BW_NOTE = 2;

function textField(name, value) {
  return { name, value: value == null ? "" : String(value), type: 0 };
}

/** entries: [{ type, title, username, url, password, notes }] -> Bitwarden JSON object */
export function entriesToBitwardenJson(entries) {
  const items = entries.map((e) => {
    const type = KNOWN_TYPES.includes(e.type) ? e.type : DEFAULT_TYPE;
    const base = {
      id: null,
      organizationId: null,
      folderId: null,
      reprompt: 0,
      name: e.title || "(no title)",
      favorite: false,
      collectionIds: null,
    };
    if (type === "login") {
      return {
        ...base,
        type: BW_LOGIN,
        notes: e.notes || null,
        fields: [textField("crm_type", type)],
        login: {
          uris: e.url ? [{ match: null, uri: e.url }] : [],
          username: e.username || null,
          password: e.password || null,
          totp: null,
        },
      };
    }
    return {
      ...base,
      type: BW_NOTE,
      notes: e.password || null,
      secureNote: { type: 0 },
      fields: [
        textField("crm_type", type),
        textField("username", e.username),
        textField("url", e.url),
        textField("crm_notes", e.notes),
      ],
    };
  });
  return { encrypted: false, folders: [], items };
}

function guessType(secret) {
  const s = (secret || "").trim();
  if (s.startsWith("{") && /"type"\s*:\s*"service_account"/.test(s)) return "firebase_service_account";
  if (/^(ghp_|gho_|ghu_|ghs_|ghr_|github_pat_)/.test(s)) return "github_pat";
  return "api_key";
}

function field(item, name) {
  const f = (item.fields || []).find((x) => x && x.name === name);
  return f && f.value != null ? String(f.value) : "";
}

/**
 * text of a Bitwarden JSON export -> { entries, skipped, error }
 * entries: [{ type, title, username, url, password, notes }]
 * skipped: [{ name, reason }]  (cards, identities, empty items)
 */
export function parseBitwardenJson(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { entries: [], skipped: [], error: "This is not a valid JSON file." };
  }
  if (data && data.encrypted === true) {
    return { entries: [], skipped: [], error: "This file is an encrypted Bitwarden export. Export it again as plain \".json\"." };
  }
  if (!data || !Array.isArray(data.items)) {
    return { entries: [], skipped: [], error: "This does not look like a Bitwarden JSON export (no \"items\")." };
  }

  const entries = [];
  const skipped = [];
  for (const item of data.items) {
    const name = (item && item.name) || "(no title)";
    if (!item) continue;

    if (item.type === BW_LOGIN) {
      const l = item.login || {};
      const uri = Array.isArray(l.uris) && l.uris.length > 0 && l.uris[0] ? l.uris[0].uri || "" : "";
      const password = l.password || "";
      const crmType = field(item, "crm_type");
      const type = KNOWN_TYPES.includes(crmType) ? crmType : "login";
      if (!password && !l.username) { skipped.push({ name, reason: "no username or password" }); continue; }
      entries.push({
        type,
        title: name,
        username: l.username || "",
        url: uri,
        password,
        notes: item.notes || "",
      });
    } else if (item.type === BW_NOTE) {
      const secret = item.notes || "";
      if (!secret.trim()) { skipped.push({ name, reason: "empty note" }); continue; }
      const crmType = field(item, "crm_type");
      const type = KNOWN_TYPES.includes(crmType) ? crmType : guessType(secret);
      entries.push({
        type,
        title: name,
        username: field(item, "username"),
        url: field(item, "url"),
        password: secret,
        notes: field(item, "crm_notes"),
      });
    } else {
      skipped.push({ name, reason: "cards and identities are not supported" });
    }
  }
  return { entries, skipped, error: "" };
}

/** Same entry = same type, title, username, url (case-insensitive on title). */
export function entryKey(e) {
  return [e.type || DEFAULT_TYPE, (e.title || "").trim().toLowerCase(), (e.username || "").trim(), (e.url || "").trim()].join("|");
}

/**
 * Split parsed entries into the ones to add and the ones already in the vault.
 * An existing entry with the same key AND the same secret counts as a duplicate;
 * same key with a different secret is added (so nothing is silently overwritten).
 */
export function splitNewAndDuplicate(parsed, existing) {
  const have = new Set(existing.map((e) => entryKey(e) + "\u0000" + (e.password || "")));
  const toAdd = [];
  const duplicates = [];
  for (const p of parsed) {
    const k = entryKey(p) + "\u0000" + (p.password || "");
    if (have.has(k)) duplicates.push(p);
    else { toAdd.push(p); have.add(k); }
  }
  return { toAdd, duplicates };
}

/** Triggers a file download in the browser. */
export function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
